import VueCookies from 'vue-cookies';
import {
  format,
  isValid,
  parse,
  getDaysInMonth,
} from 'date-fns';
import { enGB as dateFnsLocaleEnGb } from 'date-fns/locale';
import {
  isFinite,
  isInteger,
} from 'lodash';
import WebFont from 'webfontloader';
import {
  INSURER_MIN_AGE,
  DATE_OF_BIRTH_MAX_AGE,
  PARTNERS,
  MODAL_CONTENT,
} from './constants';

export const DATE_FORMAT = 'yyyy-MM-dd';

const getYearsOptions = () => {
  const end = new Date().getFullYear() - INSURER_MIN_AGE;
  const start = end - (DATE_OF_BIRTH_MAX_AGE - INSURER_MIN_AGE);
  return Array(end - start + 1).fill().map((_, idx) => start + idx).reverse();
};

export const monthsOptions = [...Array(12)].map((_, i) => ({
  value: i + 1,
  text: dateFnsLocaleEnGb.localize.month(i),
}));

export const yearsOptions = getYearsOptions();

export const processCookies = (cookiesResponse) => {
  const { status, data } = cookiesResponse;
  if (status === 'success') {
    Object.values(data).forEach(cookie => {
      if (cookie.expires.toString() === '-1') {
        VueCookies.remove(cookie.name);
      } else {
        VueCookies.set(
          cookie.name,
          cookie.value,
          (new Date(+`${cookie.expires}000`)).toUTCString(),
          cookie.path,
          cookie.domain,
        );
      }
    });
  }
}

export const checkDateObjFilled = (dateObj, fields = ['day', 'month', 'year']) => {
  return !(
    (fields.includes('day') ? (isFinite(+dateObj.day) && +dateObj.day > 0) : true) &&
    (fields.includes('month') ? isFinite(dateObj.month) : true) &&
    (fields.includes('year') ? isFinite(dateObj.year) : true)
  );
};

/**
 * Class representing Date with date fns methods.
 * @extends Date
 */
export class ExtDate extends Date {
  // private methods
  /**
   * Checks if a value is instance of Date.
   * @private
   * @param {Date} date
   * @returns {boolean}
   */
  _isDate (date) { return date instanceof Date; }

  /**
   * Returns parsed date or throw an error if no param given
   * @private
   * @param {any} date - value to parse as Date
   * @returns {*|UtcDate}
   */
  _getDate (date) {
    if (!date) {
      throw new Error('argument required');
    }
    return this._isDate(date) ? date : new Date(date);
  };
  // end private methods

  /**
   * Return the formatted date string in the given format.
   * https://date-fns.org/v2.19.0/docs/format
   * @param {String} pattern - the string of tokens
   * @returns {String} - the formatted date string
   */
  format (pattern = DATE_FORMAT) {
    return format(this, pattern);
  }

  /**
   * Return the date parsed from string using the given format string.
   * https://date-fns.org/v2.19.0/docs/parse
   * @param {String} date - the string to parse
   * @param {String} pattern - the string of tokens
   * @returns {ExtDate} - the parsed date
   */
  parse (date, pattern) {
    const milliseconds = parse(date, pattern, new Date()).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Get the number of days in a month of the given date.
   * @returns {Number} - the number of days in a month
   */
  getDaysInMonth () {
    return getDaysInMonth(this);
  }

  /**
   * Returns false if argument is Invalid Date and true otherwise. Argument is converted to Date using toDate. See toDate Invalid Date is a Date, whose time value is NaN.
   * https://date-fns.org/v2.19.0/docs/isValid
   * @returns {boolean} - the date is valid
   */
  isValid () { return isValid(this); }
}

export const getPartnerFonts = config => {
  return [1, 2, 3].map(fontNumber => {
    return config[`partnerFont${fontNumber}`];
  });
};

export const getPartnerFontsWebfontsConfig = fonts => {
  const config = {};

  fonts.forEach(font => {
    if (!font) return;
    switch (font.type) {
      case 'Google':
        if (!config.google) config.google = { families: [] };
        config.google.families.push(`${font.name}:${font.weight}`);
        break;
      case 'custom':
        if (!config.custom) config.custom = { families: [], urls: [] };
        config.custom.families.push(font.name);
        config.custom.urls.push(font.url);
    }
  });
  return Object.keys(config).length > 0 ? config : null;
};

export const processConfig = rawConfig => {
  const config = {};

  Object.keys(rawConfig).forEach(key => {
    if (
      [0, 1, '0', '1'].includes(rawConfig[key]) &&
      key !== 'account'
    ) {
      config[key] = !!rawConfig[key];
    } else if (rawConfig[key] === '') {
      // need to keep partnerFooterText as '' to keep it working with partners that have unique footers (for now only TotallyMoney)
      // if it's '' and there is no unique footer for a partner, nothing will be rendered
      config[key] = key !== 'partnerFooterText' ? null : '';
    } else if (
      [
        'partnerTheme',
        'partnerFont1',
        'partnerFont2',
        'partnerFont3',
      ].includes(key)
    ) {
      config[key] = JSON.parse(rawConfig[key]);
    } else {
      config[key] = rawConfig[key];
    }
  });

  return config;
};

export const initPartnerFonts = config => {
  const partnerFonts = getPartnerFonts(config);
  const partnerFontsConfig = getPartnerFontsWebfontsConfig(partnerFonts);

  const vars = {};

  partnerFonts.forEach((font, index) => {
    if (!font) return;
    document.querySelector(':root').style.setProperty(`--font-${index + 1}`, font.name);
    vars[`--font-${index + 1}`] = font.name;
    font.weight && document.querySelector(':root').style.setProperty(`--font-${index + 1}-weight`, font.weight);
    vars[`--font-${index + 1}-weight`] = font.weight;
  });

  partnerFontsConfig && WebFont.load(partnerFontsConfig);
};

export const getHeaderComponent = (config) => {
  const isDefaultPartner = PARTNERS.QuoteZone.includes(config.partner);
  const isCniPartner = PARTNERS.CompareNI.includes(config.partner);

  if (isDefaultPartner) {
    return () => import('@/components/SiteHeader/QzSiteHeader/QzSiteHeader.vue');
  }

  if (isCniPartner) {
    return () => import('@/components/SiteHeader/PartnersHeaders/CniSiteHeader.vue');
  }

  return () => import('@/components/SiteHeader/SiteHeader.vue');
};

export const getFooterComponent = (config) => {
  const isDefaultPartner = PARTNERS.QuoteZone.includes(config.partner);
  const isCniPartner = PARTNERS.CompareNI.includes(config.partner);

  if (isDefaultPartner) {
    return () => import('@/components/SiteFooter/QzSiteFooter.vue');
  }

  if (isCniPartner) {
    return () => import('@/components/SiteFooter/PartnersFooters/CniSiteFooter.vue');
  }

  if (config.partnerFooterText === 'AR') {
    return () => import(`@/components/SiteFooter/PartnersFooters/SiteFooterAR.vue`);
  }

  if (config.partnerFooterText === 'IAR') {
    return () => import(`@/components/SiteFooter/PartnersFooters/SiteFooterIAR.vue`);
  }

  return () => import('@/components/SiteFooter/SiteFooter.vue');
};
// common helper for creating a node element
const createElement = ({ tagName, className, attributes = {} }) => {
  const element = document.createElement(tagName);

  if (className) {
    const classNames = className.split(' ').filter(Boolean);
    element.classList.add(...classNames);
  }

  Object.keys(attributes).forEach((key) => element.setAttribute(key, attributes[key]));

  return element;
};

// Create a modal layout
const createModal = ({ title, mainText, actions }) => {
  const overlay = createElement({ tagName: 'div', className: 'custom-error overlay' });
  const modal = createElement({ tagName: 'div', className: 'modal-wrap' });
  const modalTitle = createElement({ tagName: 'h1', className: 'title' });
  const subText = createElement({ tagName: 'p', className: 'sub-text' });
  const optionsWrap = createElement({ tagName: 'div', className: 'actions-wrap' });

  modalTitle.innerText = title;
  subText.innerText = mainText;

  if (actions) {
    optionsWrap.append(actions);
  }

  modal.append(modalTitle);
  modal.append(subText);
  modal.append(optionsWrap);
  overlay.append(modal);

  return overlay;
};

// Create a link for some actions in modal
const createAction = ({ url = '', label = '' }) => {
  const link = document.createElement('a');
  link.classList.add('action');
  link.innerText = label;
  link.setAttribute('href', url);

  return link;
};

// Handle a response error and show a modal
export const initErrorHandler = ({ redirectUrl = '', status }) => {
  const root = document.body;

  root.append(createModal({
    title: MODAL_CONTENT[status].title,
    mainText: MODAL_CONTENT[status].mainText,
    actions: createAction({ url: redirectUrl, label: MODAL_CONTENT[status].btnLabel }),
  }));
};

export const addScriptToHead = (src) => {
  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;

      script.addEventListener('load', () => {
        resolve();
      });

      script.addEventListener('error', () => {
        reject();
      });

      document.head.appendChild(script);
    } catch (error) {
      reject();
    }
  });
};

export const shouldShowKeepMeSignedInButton = (partner) => {
  return [...PARTNERS.CompareNI, ...PARTNERS.QuoteZone].includes(partner);
};

export const isQuotezoneOrCNIPartner = (partner) => {
  return [...PARTNERS.CompareNI, ...PARTNERS.QuoteZone].includes(partner);
};

export const isQuotezonePartner = (partner) => {
  return [...PARTNERS.QuoteZone].includes(partner);
};

export const prepareLink = (link, baseUrl) => {
  return link.startsWith('http') ? link : baseUrl + link;
};
